import React, { useState, useEffect, useRef } from 'react';
import CodeMirror from '@uiw/react-codemirror';
import { python } from '@codemirror/lang-python';
import './IdePage.css';

// Keep only: the "File … line …" line, the code line, and the final error line.
function extractPyError(errLike) {
  const text = String(
    (errLike && typeof errLike.toString === 'function')
      ? errLike.toString()
      : (errLike?.message ?? errLike)
  ).replace(/\r/g, '');

  const lines = text.split('\n').filter(Boolean);

  // Find the last Python "File ..." frame (pyodide traceback)
  let i = lines.length - 1;
  let fileIdx = -1;
  for (; i >= 0; i--) {
    if (lines[i].startsWith('  File ') || lines[i].startsWith('File ')) {
      fileIdx = i;
      break;
    }
  }

  const last = lines[lines.length - 1] || '';

  if (fileIdx === -1) {
    // Fallback: if no python frame found, show the last 1–2 lines
    // (avoids showing the long JS wasm wrapper stack)
    return lines.slice(-2).join('\n');
  }

  const fileLine  = lines[fileIdx];
  const codeLine  = lines[fileIdx + 1] || '';
  const errorLine = last;

  return [fileLine, codeLine, errorLine].filter(Boolean).join('\n');
}



// Controlled IDE Component
function IDE({ value = '', onChange, onSubmit, showInlineSubmit = false, readOnly = false }) {
  // Console + runtime state
  const [output, setOutput] = useState([]);
  const [isPyodideReady, setIsPyodideReady] = useState(false);
  const [consoleHeight, setConsoleHeight] = useState(() => {
    const saved = Number(localStorage.getItem('rt_ide_console_h'));
    return Number.isFinite(saved) && saved > 40 ? saved : 200;
  });
  const [notification, setNotification] = useState({ isVisible: false, message: '', type: '' });
  const pyodide = useRef(null);

  // Keep a ref to the latest code (so run uses the freshest text)
  const codeRef = useRef(value);
  useEffect(() => { codeRef.current = value; }, [value]);

  // Auto-hide notification
  useEffect(() => {
    if (notification.isVisible) {
      const timer = setTimeout(() => {
        setNotification((prev) => ({ ...prev, isVisible: false }));
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  const loadPyodideScript = () => {
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/pyodide/v0.25.0/full/pyodide.js';
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Failed to load the Pyodide script.'));
      document.body.appendChild(script);
    });
  };

  async function setupPyodide() {
    setOutput([{ type: 'log', message: 'Initializing Python environment...' }]);
    try {
      await loadPyodideScript();
      pyodide.current = await window.loadPyodide({
        indexURL: "https://cdn.jsdelivr.net/pyodide/v0.25.0/full/",
      });
      setOutput((prev) => [...prev, { type: 'log', message: 'Python environment ready.' }]);
      setIsPyodideReady(true);
    } catch (error) {
      setOutput((prev) => [...prev, { type: 'error', message: `Failed to initialize Python: ${error.message}` }]);
    }
  }

  useEffect(() => {
    if (!window.loadPyodide) {
      setupPyodide();
    } else {
      // If already loaded elsewhere, just wire it up once
      (async () => {
        try {
          pyodide.current = await window.loadPyodide({
            indexURL: "https://cdn.jsdelivr.net/pyodide/v0.25.0/full/",
          });
          setIsPyodideReady(true);
        } catch {}
      })();
    }
    // no deps: initialize once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRun = async () => {
    if (!pyodide.current) return;
    const newOutput = [];
    setOutput([]);
    try {
      pyodide.current.globals.set("print", (...args) => {
        const message = args.map((arg) => String(arg)).join(' ');
        newOutput.push({ type: 'log', message });
      });
      await pyodide.current.runPythonAsync(codeRef.current);
      setOutput(newOutput);
    } catch (error) {
      const pretty = extractPyError(error);
      setOutput([...newOutput, { type: 'error', message: pretty }]);
    }
  };

  const handleMouseDown = (e) => {
    e.preventDefault();
    const handleMouseMove = (moveEvent) => {
      const newHeight = window.innerHeight - moveEvent.clientY;
      if (newHeight > 40 && newHeight < window.innerHeight - 80) {
        setConsoleHeight(newHeight);
        try { localStorage.setItem('rt_ide_console_h', String(newHeight)); } catch {}
      }
    };
    const handleMouseUp = () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
  };

  const handleSubmit = () => {
    if (typeof onSubmit === 'function') {
      onSubmit(codeRef.current);
    }
  };

  return (
    <div className="ide-layout">
      {/* Optional toast UI */}
      {/* {notification.isVisible && (
        <div className={`notification-banner ${notification.type}`}>
          {notification.message}
        </div>
      )} */}

      {/* <button
        onClick={handleRun}
        className="run-button"
        disabled={!isPyodideReady}
      >
        {isPyodideReady ? 'Run ►' : 'Loading...'}
      </button> */}

      <div className="editor-pane">
        <CodeMirror
          value={value}
          height="100%"
          extensions={[python()]}
          onChange={(v) => onChange?.(v)}
          theme="dark"
          style={{ height: '100%' }}
          editable={!readOnly}
        />
      </div>

      <div className="resizer" onMouseDown={handleMouseDown} />

      <div className="console-pane" style={{ height: `${consoleHeight}px` }}>
        <pre className="console-output">
          {output.map((line, index) => (
            <div key={index} className={`console-line ${line.type}`}>
              {line.message}
            </div>
          ))}
        </pre>
      </div>

      <div className={`ide-floating-controls ${showInlineSubmit ? 'with-inline-submit' : ''}`}>
        <button
          onClick={handleRun}
          className="run-button"
          disabled={!isPyodideReady || readOnly}
        >
          {isPyodideReady ? 'Run ►' : 'Loading...'}
        </button>
        {showInlineSubmit && (
          <button onClick={handleSubmit} className="ide-inline-submit-btn" disabled={readOnly}>
            Submit
          </button>
        )}
      </div>

    </div>
  );
}

export default IDE;
